import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { AuditView, Page } from '@/api/types';
import { Card, ErrorNote, Spinner, Timestamp } from '@/components/Common';

export function AuditPage() {
  const [page, setPage] = useState(0);
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');

  const query = new URLSearchParams({ page: String(page), size: '100' });
  if (action) query.set('action', action);
  if (actor) query.set('actor', actor);

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit', query.toString()],
    queryFn: () => api.get<Page<AuditView>>(`/api/admin/audit?${query}`),
    placeholderData: keepPreviousData,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  return (
    <Card
      title={`Audit log — ${data?.totalElements.toLocaleString() ?? 0} entries`}
      actions={
        <div className="flex gap-2">
          <input className="input w-44" placeholder="Filter by actor" value={actor}
                 onChange={(e) => { setActor(e.target.value); setPage(0); }} />
          <input className="input w-44" placeholder="Filter by action" value={action}
                 onChange={(e) => { setAction(e.target.value); setPage(0); }} />
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">When</th><th className="th">Actor</th><th className="th">Action</th>
              <th className="th">Entity</th><th className="th">Detail</th><th className="th">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(data?.content ?? []).map((entry) => (
              <tr key={entry.id} className="hover:bg-ink-50">
                <td className="td"><Timestamp value={entry.createdAt} /></td>
                <td className="td">{entry.actor}</td>
                <td className="td mono">{entry.action}</td>
                <td className="td text-xs text-ink-600">
                  {entry.entityType}
                  {entry.entityId && (
                    <span className="ml-1 mono">{entry.entityId.slice(0, 8)}…</span>
                  )}
                </td>
                <td className="td max-w-md truncate text-xs text-ink-600"
                    title={JSON.stringify(entry.detail)}>
                  {entry.detail ? JSON.stringify(entry.detail) : '—'}
                </td>
                <td className="td mono text-xs">{entry.ip ?? '—'}</td>
              </tr>
            ))}
            {(data?.content ?? []).length === 0 && (
              <tr><td className="td py-8 text-center text-ink-500" colSpan={6}>
                Nothing matches.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-ink-200 px-4 py-2.5">
        <span className="text-xs text-ink-500">
          Page {(data?.page ?? 0) + 1} of {Math.max(1, data?.totalPages ?? 1)}
        </span>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}>Previous</button>
          <button type="button" className="btn-secondary"
                  disabled={(data?.page ?? 0) + 1 >= (data?.totalPages ?? 1)}
                  onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </Card>
  );
}
